import React, { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { PUBLIC_SEND_NE_FORM } from 'Queries/Mutations';
import { useNEFISteps } from 'context/NEFIStepsContext';
import { FormikProvider, useFormik } from 'formik';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import PropTypes from 'prop-types';

import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';
import { DataPolicyContent } from 'components/DataPolicy/DataPolicyContent';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { SubmitPopup } from 'components/FormHelper/SubmitPopup';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import {
  createEmailSubjectForNEFI,
  getInstallerEntryByBranchSelection,
  prepNEFIDataForBackend,
} from 'components/NEFIForm/Utils/NEFIDataUtils';

import { NE_ACTIVE_STEP } from 'global/constants';

import { FFormDebug } from 'utils/FFormDebug';
import { checkKeyDown } from 'utils/FormUtils';
import { getTenantLandingPage } from 'utils/UrlUtil';

import { useNEFIFormUploadIV } from './NEFIFormUploadIV';

import './NEFIStepFormUpload.sass';

const NEFI_DATAPOLICY_CONFIRMATION_TEXT =
  'Mit Absenden dieser Anfrage bestätige ich / bestätigen wir, dass ich / wir die „Datenschutz“-Zustimmung und die hier aktuell hinterlegte „Informationen zum Datenschutz“ zur Kenntnis genommen habe / haben und mit Absenden dieser Anfrage bestätige ich / bestätigen wir, dass die im Antrag genannten Personen die „Informationen zum Datenschutz“ gelesen haben und mit den in der „Datenschutz“-Zustimmung genannten Punkten einverstanden sind.';

export const NEFIStepFormUpload = ({ dataPolicyDocuments }) => {
  const { activeStep, setActiveStep, getSubmittedValuesWithStep } = useNEFISteps();
  const [response, setResponse] = useState('');
  const [submitPopupOpen, setSubmitPopupOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sendNeForm] = useMutation(PUBLIC_SEND_NE_FORM, PUBLIC_NO_AUTH_CONFIG);

  const branchSelectionValues = getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION);
  const installerEntry = useMemo(
    () => getInstallerEntryByBranchSelection(branchSelectionValues),
    [branchSelectionValues]
  );

  const { initialValues, validationSchema } = useNEFIFormUploadIV(branchSelectionValues);
  const { comboBoxOptions } = useFormUtilityData();
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const { values, errors, touched, handleSubmit } = formik;
  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const onBack = () => {
    setActiveStep(NE_ACTIVE_STEP.FORM);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    const subject = createEmailSubjectForNEFI(
      installerEntry,
      getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)
    );

    const formData = prepNEFIDataForBackend(
      getSubmittedValuesWithStep(NE_ACTIVE_STEP.FORM),
      formik.values,
      subject,
      [],
      getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION),
      comboBoxOptions
    );

    sendNeForm({
      variables: {
        neInput: formData,
      },
    })
      .then(({ data }) => {
        setSubmitPopupOpen(true);
        setResponse(data.ne.status);
      })
      .catch(() => {
        setSubmitPopupOpen(true);
        setResponse('ERROR');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (activeStep !== NE_ACTIVE_STEP.FORM_UPLOAD) return null;

  return (
    <>
      <div className="nefi-step-form-upload">
        <FormikProvider value={formik}>
          <form onKeyDown={checkKeyDown} onSubmit={handleSubmit}>
            <FormPageWrapper>
              <FormSection key="changesAndPrivacy" title="Datenschutz">
                <div>Hier können Sie ihre Zustimmungen bearbeiten</div>
                <div className="nefi-agreement-section__wrapper">
                  <DataPolicyContent
                    confirmationText={NEFI_DATAPOLICY_CONFIRMATION_TEXT}
                    dsgvoAddressPublic={values.isAddressPublic}
                    dsgvoCanSendMail={values.canSendMail}
                    dsgvoAddressPublicName="isAddressPublic"
                    dsgvoCanSendMailName="canSendMail"
                    onChangeDsvgoAddressPublic={formik.handleChange}
                    onChangeDsvgoCanSendMail={formik.handleChange}
                    documents={dataPolicyDocuments}
                    showAddressPublicApproval={false}
                  />
                </div>
              </FormSection>
            </FormPageWrapper>
          </form>
        </FormikProvider>
        <FFormDebug values={values} touched={touched} errors={errors} />
      </div>
      <div className="nefi-form__action-buttons">
        {isSubmitting ? (
          <LoadingIndicator />
        ) : (
          <FormActionButtons
            hasErrors={hasErrors}
            titleLeft="Zurück"
            titleRight={activeStep === NE_ACTIVE_STEP.FORM_UPLOAD ? 'Antrag einreichen' : 'Weiter'}
            onClickLeft={onBack}
            onClickRight={onSubmit}
            errorMessage="Bitte füllen Sie alle mit * gekennzeichneten Pflichtfelder aus."
          />
        )}
      </div>
      <SubmitPopup
        type="NEW_ENTRY"
        loading={isSubmitting}
        popupOpen={submitPopupOpen}
        setPopupOpen={() => setSubmitPopupOpen(false)}
        response={response}
        redirectFullOnSuccessOrCancel={getTenantLandingPage()}
        redirectCancelButtonText="Zurück zum Login"
        onErrorRetry={() => {
          onSubmit();
        }}
      />
    </>
  );
};

NEFIStepFormUpload.propTypes = {
  dataPolicyDocuments: PropTypes.array,
};

NEFIStepFormUpload.defaultProps = {
  dataPolicyDocuments: [],
};
